import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { MotifHeader, MotifHeaderLogo } from "@ey-xd/motif-react";
import { useLogoutUserMutation } from "common/redux/user/mutations";
import { auth0SessionCache } from "common/services/auth0-session-cache";
import { MainRoutes, ROUTES } from "common/config/route.config";
import Loader from "common/components/custom/loader/Loader";
import AsyncAvatar from "common/components/custom/async-avatar/AsyncAvatar";
import { IconCollection } from "common/config/media.config";
import { RootState } from "common/redux/core/root.reducer";
import useOnClickOutside from "common/hooks/useOnClickOutside";
import useProfile from "common/hooks/useProfile";
import NotificationsDropdown from "common/components/custom/notifications-dropdown/NotificationsDropdown";

import { eyLogo, IMAGE_COLLECTION } from "assets/media/images";
import IconButton from "../icon-button";
import { useTranslation } from "react-i18next";
import commonKeysConfig from "common/config/common-keys.config";
import EngagementSwitcher from "../../custom/engagement-switcher/EngagementSwitcher";
import Icon from "../icon";

import "./Header.scss";

type HeaderProps = {
  fixed?: boolean;
  hideContainer?: boolean;
  showEngagementSelection?: boolean;
  showNotifications?: boolean;
  showContactSupport?: boolean;
  onToggleSideNav?: () => void;
};

const Header = ({
  fixed,
  hideContainer = false,
  showEngagementSelection = false,
  showNotifications = false,
  showContactSupport = true,
  onToggleSideNav
}: HeaderProps) => {
  const {
    id: userId,
    displayName,
    mail,
    jobTitle,
    mobilePhone,
    officeLocation
  } = useProfile();
  const { isSuperAdmin } = useSelector((state: RootState) => state.user);

  const [showUserProfile, setShowUserProfile] = useState(false);
  const { t: tCommon } = useTranslation("common");
  const navigate = useNavigate();
  const userProfileRef = useRef(null);

  useOnClickOutside(userProfileRef, () => {
    if (showUserProfile) {
      setShowUserProfile(false);
    }
  });

  const [
    logoutUser,
    {
      data: logoutUserData,
      isSuccess: isLogoutUserSucess,
      isError: isLogoutUserError,
      isLoading: isLogoutUserLoading
    }
  ] = useLogoutUserMutation();

  useEffect(() => {
    // if (isLogoutUserSucess && logoutUserData?.isSuccess) {
    if (isLogoutUserSucess) {
      sessionStorage.clear();
      localStorage.clear();
      auth0SessionCache.notifyLogout();
      navigate(MainRoutes.LOGOUT);
    }
  }, [isLogoutUserSucess, logoutUserData]);

  const handleLogout = () => {
    logoutUser({});
  };

  const handleUserMgmtClick = () => {
    navigate(MainRoutes.USER_MGMT);
    setShowUserProfile(false);
  };

  const handlePrivacyPolicyClick = () => {
    navigate(MainRoutes.PRIVACY_POLICY);
    setShowUserProfile(false);
  };

  const handleContactUsClick = () => {
    navigate(MainRoutes.CONTACT_SUPPORT);
  };

  return (
    <>
      {isLogoutUserLoading && <Loader />}
      <MotifHeader
        appHeaderName={
          <h1 className="motif-header-app-name">
            {tCommon(commonKeysConfig.APP_NAME)}
          </h1>
        }
        logo={
          <MotifHeaderLogo>
            <a>
              <img
                src={eyLogo}
                alt=""
                onClick={() => navigate(ROUTES.HOME.INDEX)}
              />
            </a>
          </MotifHeaderLogo>
        }
        iconsContainer={
          <div className="icon-container">
            {!hideContainer ? (
              <>
                {showNotifications && <NotificationsDropdown />}

                {showContactSupport && (
                  <IconButton
                    className="contact-support-button"
                    onClick={handleContactUsClick}
                    aria-label={tCommon(commonKeysConfig.a11yContactSupport)}
                  >
                    <Icon src={IconCollection.IC_HEADSET_HELP} />
                  </IconButton>
                )}

                {showEngagementSelection && <EngagementSwitcher />}

                <IconButton aria-label="Click to open menu" type="reset">
                  <div
                    className={`avatar-wrapper ${
                      showUserProfile ? "active" : ""
                    }`}
                  >
                    <div
                      className="user-avatar"
                      onClick={() => setShowUserProfile(!showUserProfile)}
                    >
                      <AsyncAvatar
                        id={userId}
                        name={displayName}
                        size="small"
                      />
                      <img src={IMAGE_COLLECTION.IC_ARROW_DOWN} alt="" />
                    </div>
                    {showUserProfile && (
                      <div
                        className="user-details-wrapper"
                        ref={userProfileRef}
                      >
                        <header>
                          <AsyncAvatar
                            id={userId}
                            name={displayName}
                            size="large"
                          />
                          <span className="user-name">{displayName}</span>
                          <span className="user-job">{jobTitle}</span>
                          <img
                            src={IMAGE_COLLECTION.CLOSE}
                            alt="Close"
                            className="close"
                            onClick={() => setShowUserProfile(false)}
                          />
                        </header>
                        <div className="details">
                          <div className="details__list">
                            <span>{tCommon(commonKeysConfig.phone)}</span>
                            <h6>{mobilePhone ? mobilePhone : "--"}</h6>
                          </div>
                          <div className="details__list">
                            <span>{tCommon(commonKeysConfig.email)}</span>
                            <h6>{mail}</h6>
                          </div>
                          <div className="details__list">
                            <span>{tCommon(commonKeysConfig.location)}</span>
                            <h6>{officeLocation}</h6>
                          </div>
                        </div>

                        {isSuperAdmin && (
                          <div
                            className="user-mgmt-btn"
                            onClick={() => handleUserMgmtClick()}
                          >
                            <Icon src={IconCollection.IC_GROUP} />
                            <span>{tCommon(commonKeysConfig.userMgmt)}</span>
                          </div>
                        )}

                        <div
                          className="privacy-policy-btn"
                          onClick={handlePrivacyPolicyClick}
                        >
                          <Icon src={IconCollection.IC_PRIVACY_POLICY} />
                          <span>{tCommon(commonKeysConfig.privacyPolicy)}</span>
                        </div>

                        {/* <div className="theme">
                          <MotifToggleSwitch
                            id="theme-switcher"
                            offLabel="Light"
                            onChange={function Ua() { }}
                            onLabel="Dark"
                          />
                        </div> */}
                        <footer>
                          <img src={IMAGE_COLLECTION.IC_SIGNOUT} alt="" />
                          <h5 onClick={handleLogout}>
                            {tCommon(commonKeysConfig.signOut)}
                          </h5>
                        </footer>
                      </div>
                    )}
                  </div>
                </IconButton>
              </>
            ) : (
              <></>
            )}
          </div>
        }
      />
    </>
  );
};

export default Header;
